import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend
} from "recharts";

// ─── Maghil Design Tokens ────────────────────────────────────────────────────
const T = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  card: "#FFFFFF",
  cardBorder: "#E2E5EC",
  hover: "#F0F1F5",
  accent: "#D23A3B",
  accentSoft: "rgba(210,58,59,0.08)",
  accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A",
  greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626",
  redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706",
  orangeSoft: "rgba(217,119,6,0.08)",
  blue: "#2563EB",
  blueSoft: "rgba(37,99,235,0.08)",
  t1: "#1A1D2E",
  t2: "#5A6178",
  t3: "#9CA3B8",
  divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  cardShadow: "0 1px 4px rgba(0,0,0,0.05)",
  sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// ─── Material Symbols Rounded Icon ───────────────────────────────────────────
function MIcon({ name, size = 20, fill = 0, weight = 400, color = T.t3, style = {} }: any) {
  return (
    <span
      className="material-symbols-rounded"
      style={{
        fontSize: size,
        color,
        fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${size}`,
        lineHeight: 1,
        userSelect: "none",
        ...style,
      }}
    >
      {name}
    </span>
  );
}

// ─── Mock Data ───────────────────────────────────────────────────────────────
const MOCK = {
  eventName: "Thanksgiving 2025",
  eventDates: "Nov 22 – Nov 28, 2025",
  selectedLocation: "All Locations (5)",
  kpis: {
    totalRevenue: { value: "$48,320", delta: "+18.4%", up: true },
    totalOrders: { value: "612", delta: "+22.1%", up: true },
    avgOrderValue: { value: "$78.95", delta: "+6.3%", up: true },
    cancellations: { value: "18", delta: "-4.2%", up: true },
    fulfillmentRate: { value: "96.8%", delta: "+1.2%", up: true },
    noShows: { value: "7", delta: "-3", up: true },
  },
  itemQuantities: [
    { name: "Whole Roasted Turkey (12lb)", qty: 142, revenue: 7100 },
    { name: "Honey Glazed Ham", qty: 98, revenue: 5880 },
    { name: "Pumpkin Pie (Whole)", qty: 187, revenue: 3740 },
    { name: "Cranberry Walnut Stuffing", qty: 156, revenue: 2340 },
    { name: "Mashed Potatoes (Family)", qty: 201, revenue: 3015 },
    { name: "Green Bean Casserole", qty: 134, revenue: 1876 },
    { name: "Gravy (Quart)", qty: 178, revenue: 1424 },
    { name: "Dinner Rolls (Dozen)", qty: 165, revenue: 1320 },
    { name: "Sweet Potato Casserole", qty: 112, revenue: 1680 },
    { name: "Pecan Pie (Whole)", qty: 89, revenue: 2225 },
    { name: "Apple Pie (Whole)", qty: 76, revenue: 1520 },
    { name: "Mac & Cheese (Family)", qty: 94, revenue: 1410 },
  ],
  pickupSlots: [
    { slot: "10:00 AM", orders: 12, capacity: 25, pct: 48 },
    { slot: "10:30 AM", orders: 18, capacity: 25, pct: 72 },
    { slot: "11:00 AM", orders: 22, capacity: 25, pct: 88 },
    { slot: "11:30 AM", orders: 25, capacity: 25, pct: 100 },
    { slot: "12:00 PM", orders: 24, capacity: 25, pct: 96 },
    { slot: "12:30 PM", orders: 20, capacity: 25, pct: 80 },
    { slot: "1:00 PM", orders: 31, capacity: 30, pct: 103 },
    { slot: "1:30 PM", orders: 28, capacity: 30, pct: 93 },
    { slot: "2:00 PM", orders: 35, capacity: 30, pct: 117 },
    { slot: "2:30 PM", orders: 29, capacity: 30, pct: 97 },
    { slot: "3:00 PM", orders: 42, capacity: 35, pct: 120 },
    { slot: "3:30 PM", orders: 38, capacity: 35, pct: 109 },
    { slot: "4:00 PM", orders: 52, capacity: 40, pct: 130 },
    { slot: "4:30 PM", orders: 48, capacity: 40, pct: 120 },
    { slot: "5:00 PM", orders: 55, capacity: 40, pct: 138 },
    { slot: "5:30 PM", orders: 36, capacity: 40, pct: 90 },
    { slot: "6:00 PM", orders: 22, capacity: 30, pct: 73 },
    { slot: "6:30 PM", orders: 14, capacity: 30, pct: 47 },
  ],
  bookingCurve: [
    { day: "D-14", current: 12, prior: 8 },
    { day: "D-13", current: 18, prior: 14 },
    { day: "D-12", current: 31, prior: 22 },
    { day: "D-11", current: 48, prior: 35 },
    { day: "D-10", current: 72, prior: 52 },
    { day: "D-9", current: 98, prior: 71 },
    { day: "D-8", current: 135, prior: 96 },
    { day: "D-7", current: 189, prior: 132 },
    { day: "D-6", current: 248, prior: 178 },
    { day: "D-5", current: 312, prior: 228 },
    { day: "D-4", current: 385, prior: 296 },
    { day: "D-3", current: 458, prior: 362 },
    { day: "D-2", current: 532, prior: 421 },
    { day: "D-1", current: 586, prior: 468 },
    { day: "D-0", current: 612, prior: 502 },
  ],
  channelMix: [
    { name: "Maghil Online", value: 62, color: T.accent },
    { name: "Phone Orders", value: 18, color: T.orange },
    { name: "Walk-In Pre-Order", value: 12, color: T.green },
    { name: "Third-Party", value: 8, color: T.t3 },
  ],

  fulfillmentStatus: [
    { status: "Picked Up", count: 542, pct: 88.6, color: T.green },
    { status: "Ready", count: 28, pct: 4.6, color: T.accent },
    { status: "Preparing", count: 17, pct: 2.8, color: T.orange },
    { status: "No-Show", count: 7, pct: 1.1, color: T.red },
    { status: "Cancelled", count: 18, pct: 2.9, color: T.t3 },
  ],
  customerRunSheet: [
    { id: "ORD-2847", name: "Sarah Mitchell", phone: "(201) 555-0142", email: "s.mitchell@email.com", slot: "4:00 PM", items: "Turkey (12lb), Pumpkin Pie, Gravy x2", total: "$142.50", payment: "Fully Paid", fulfillment: "Ready" },
    { id: "ORD-2848", name: "James Rodriguez", phone: "(201) 555-0198", email: "j.rodriguez@email.com", slot: "4:00 PM", items: "Ham, Mac & Cheese, Rolls x2", total: "$98.75", payment: "Fully Paid", fulfillment: "Picked Up" },
    { id: "ORD-2849", name: "Linda Chen", phone: "(201) 555-0267", email: "l.chen@email.com", slot: "4:30 PM", items: "Turkey (12lb), Stuffing, Green Bean Casserole, Pecan Pie", total: "$168.00", payment: "Deposit Paid", fulfillment: "Preparing" },
    { id: "ORD-2850", name: "Michael Thompson", phone: "(201) 555-0334", email: "m.thompson@email.com", slot: "5:00 PM", items: "Turkey (12lb), Mashed Potatoes, Gravy, Rolls", total: "$124.25", payment: "Fully Paid", fulfillment: "Ordered" },
    { id: "ORD-2851", name: "Emily Davis", phone: "(201) 555-0089", email: "e.davis@email.com", slot: "5:00 PM", items: "Ham, Sweet Potato Casserole, Apple Pie", total: "$112.50", payment: "Fully Paid", fulfillment: "No-Show" },
    { id: "ORD-2852", name: "Robert Kim", phone: "(201) 555-0421", email: "r.kim@email.com", slot: "3:00 PM", items: "Turkey (12lb), Stuffing, Cranberry Sauce, Wine x2", total: "$196.00", payment: "Fully Paid", fulfillment: "Picked Up" },
    { id: "ORD-2853", name: "Jessica Park", phone: "(201) 555-0556", email: "j.park@email.com", slot: "3:30 PM", items: "Ham, Mashed Potatoes, Green Bean Casserole", total: "$89.50", payment: "Unpaid", fulfillment: "Cancelled" },
    { id: "ORD-2854", name: "David Wilson", phone: "(201) 555-0612", email: "d.wilson@email.com", slot: "4:30 PM", items: "Turkey (12lb), Pumpkin Pie x2, Dinner Rolls x3", total: "$155.75", payment: "Fully Paid", fulfillment: "Ready" },
  ],
};

// ─── Navigation Config ───────────────────────────────────────────────────────
const NAV_GROUPS = [
  { emoji: "📊", label: "Sales", items: ["Sales Summary", "Hourly Sales", "Order Channels"] },
  { emoji: "🍽️", label: "Menu", items: ["Item Sales", "Category Sales", "Discounts & Offers"] },
  { emoji: "👥", label: "Team", items: ["Staff Performance", "Voids & Exceptions", "Refunds"] },
  { emoji: "💰", label: "Payments", items: ["Tender Types", "EOD Reconciliation"] },
  { emoji: "🪑", label: "Guests", items: ["Check-In Analytics", "Party & Wait Times"] },
  { emoji: "📍", label: "Locations", items: ["Location Benchmark"] },
  { emoji: "🎉", label: "Events", items: ["Events & Pre-Order"], active: true },
];

// ─── Reusable Components ─────────────────────────────────────────────────────

function Card({ children, style = {}, onClick }: any) {
  return (
    <div
      onClick={onClick}
      style={{
        background: T.card,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 14,
        padding: "22px",
        boxShadow: T.cardShadow,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function DrillCard({ children, style = {}, onClick }: any) {
  return (
    <div
      onClick={onClick}
      style={{
        background: T.card,
        border: `1px solid rgba(210,58,59,0.20)`,
        borderRadius: 14,
        padding: "22px",
        boxShadow: T.cardShadow,
        cursor: "pointer",
        position: "relative",
        ...style,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 10, right: 10, opacity: 0.45 }}>
        <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {children}
    </div>
  );
}

function KpiCard({ label, value, delta, up, icon, drillable = false }: any) {
  const Wrapper = drillable ? DrillCard : Card;
  const deltaColor = up ? T.green : T.red;
  const deltaArrow = up ? "↑" : "↓";
  return (
    <Wrapper style={{ flex: 1, minWidth: 180, position: "relative", overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          top: -10,
          right: -10,
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: up ? T.greenSoft : T.redSoft,
          filter: "blur(20px)",
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative" }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
        {icon && <MIcon name={icon} size={18} color={T.t3} />}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginTop: 6, position: "relative" }}>{value}</div>
      {delta && (
        <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 4, position: "relative" }}>
          {deltaArrow} {delta} vs prior year
        </div>
      )}
    </Wrapper>
  );
}

function SectionTitle({ children, subtitle, right }: any) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>{children}</div>
        {subtitle && <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

function StatusPill({ status }: any) {
  const map = {
    "Fully Paid": { bg: T.greenSoft, color: T.green },
    "Deposit Paid": { bg: T.orangeSoft, color: T.orange },
    "Unpaid": { bg: T.redSoft, color: T.red },
    "Refunded": { bg: T.hover, color: T.t3 },
    "Picked Up": { bg: T.greenSoft, color: T.green },
    "Ready": { bg: T.accentSoft, color: T.accent },
    "Preparing": { bg: T.orangeSoft, color: T.orange },
    "Ordered": { bg: T.hover, color: T.t2 },
    "No-Show": { bg: T.redSoft, color: T.red },
    "Cancelled": { bg: T.hover, color: T.t3 },
  };
  const s = map[status] || { bg: T.hover, color: T.t3 };
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
}

function FilterPill({ label, active, onClick }: any) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 11.5,
        fontWeight: 500,
        fontFamily: T.font,
        color: active ? T.accent : T.t2,
        background: active ? T.accentSoft : "transparent",
        border: "none",
        borderRadius: 6,
        padding: "4px 12px",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function MTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 12px", boxShadow: T.cardShadow, fontFamily: T.font }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 10, color: T.t2, display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ width: 6, height: 6, borderRadius: 1, background: p.color, flexShrink: 0 }} />
          {p.name}: <span style={{ fontWeight: 600, color: T.t1 }}>{typeof p.value === "number" && p.name?.includes("$") ? `$${p.value.toLocaleString()}` : p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Section: Sidebar ────────────────────────────────────────────────────────

function Sidebar() {
  const [expandedGroup, setExpandedGroup] = useState("Events");
  return (
    <div style={{ width: 250, minHeight: "100vh", background: T.surface, borderRight: `1px solid ${T.divider}`, boxShadow: T.sidebarShadow, display: "flex", flexDirection: "column", position: "sticky", top: 0, alignSelf: "flex-start", flexShrink: 0 }}>
      {/* Logo */}
      <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #D23A3B, #E86B6B)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 700 }}>M</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>Maghil</div>
            <div style={{ fontSize: 10, color: T.t3, letterSpacing: 0.5 }}>ANALYTICS</div>
          </div>
        </div>
      </div>

      {/* Outlet Selector */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.hover, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}>
          <span style={{ fontSize: 12 }}>📍</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, flex: 1 }}>{MOCK.selectedLocation}</span>
          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.t3} strokeWidth="1.3" strokeLinecap="round" /></svg>
        </div>
      </div>

      {/* Nav Groups */}
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px" }}>
        {NAV_GROUPS.map((g) => (
          <div key={g.label} style={{ marginBottom: 2 }}>
            <div
              onClick={() => setExpandedGroup(expandedGroup === g.label ? null : g.label)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: expandedGroup === g.label ? T.accentSoft : "transparent" }}
            >
              <span style={{ fontSize: 14 }}>{g.emoji}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: expandedGroup === g.label ? T.accent : T.t2, flex: 1 }}>{g.label}</span>
              <svg width="10" height="10" viewBox="0 0 10 10" style={{ transform: expandedGroup === g.label ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
                <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={expandedGroup === g.label ? T.accent : T.t3} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {expandedGroup === g.label && (
              <div style={{ paddingLeft: 22, paddingTop: 2, paddingBottom: 4 }}>
                {g.items.map((item, idx) => {
                  const isActive = g.active && item === "Events & Pre-Order";
                  return (
                    <div
                      key={idx}
                      style={{
                        fontSize: 12,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? T.accent : T.t2,
                        padding: "6px 10px",
                        borderRadius: 6,
                        background: isActive ? T.accentSoft : "transparent",
                        borderLeft: isActive ? `2px solid ${T.accent}` : "2px solid transparent",
                        cursor: "pointer",
                        marginBottom: 1,
                      }}
                    >
                      {item}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "14px 16px", borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>JM</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>James Murray</div>
          <div style={{ fontSize: 10, color: T.t3 }}>General Manager</div>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Top Bar ────────────────────────────────────────────────────────

function TopBar() {
  const [activeFilter, setActiveFilter] = useState("Event Range");
  return (
    <div style={{ height: 56, background: T.surface, borderBottom: `1px solid ${T.divider}`, boxShadow: T.topbarShadow, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", position: "sticky", top: 0, zIndex: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, color: T.t3 }}>Reports</span>
        <span style={{ fontSize: 12, color: T.t3 }}>›</span>
        <span style={{ fontSize: 12, color: T.t3 }}>Events</span>
        <span style={{ fontSize: 12, color: T.t3 }}>›</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>Events & Pre-Order</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Event Selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.accentSoft, border: `1px solid rgba(210,58,59,0.15)`, borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>
          <MIcon name="celebration" size={14} color={T.accent} fill={1} />
          <span style={{ fontSize: 11.5, fontWeight: 600, color: T.accent }}>{MOCK.eventName}</span>
          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.accent} strokeWidth="1.3" strokeLinecap="round" /></svg>
        </div>
        {/* Date */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 6, background: T.hover }}>
          <MIcon name="calendar_today" size={13} color={T.t3} />
          <span style={{ fontSize: 11.5, fontWeight: 500, color: T.t2 }}>{MOCK.eventDates}</span>
        </div>
        {/* Compare Toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer" }}>
          <MIcon name="compare_arrows" size={14} color={T.t3} />
          <span style={{ fontSize: 11.5, fontWeight: 400, color: T.t2 }}>vs 2024</span>
        </div>
        {/* Export */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer" }}>
          <MIcon name="download" size={14} color={T.t3} />
          <span style={{ fontSize: 11.5, fontWeight: 400, color: T.t2 }}>Export</span>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Prep Quantity Chart ────────────────────────────────────────────

function PrepQuantityChart() {
  const data = MOCK.itemQuantities;
  const maxQty = Math.max(...data.map(d => d.qty));
  return (
    <Card style={{ flex: 2 }}>
      <SectionTitle
        subtitle="Confirmed units by item — event period"
        right={
          <div style={{ display: "flex", gap: 6 }}>
            <FilterPill label="By Quantity" active />
            <FilterPill label="By Revenue" />
          </div>
        }
      >
        Prep Quantity List
      </SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: T.t1, minWidth: 180, textAlign: "right", flexShrink: 0 }}>{item.name}</span>
            <div style={{ flex: 1, height: 14, background: T.hover, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(item.qty / maxQty) * 100}%`, background: i === 0 ? T.accent : "rgba(210,58,59,0.55)", borderRadius: 4, transition: "width 0.3s ease" }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, minWidth: 36, textAlign: "right" }}>{item.qty}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Section: Pickup Slot Capacity ───────────────────────────────────────────

function PickupSlotChart() {
  const data = MOCK.pickupSlots;
  const peak = Math.max(...data.map(d => d.orders));
  return (
    <Card style={{ flex: 2 }}>
      <SectionTitle subtitle="Order volume by pickup time slot">
        Pickup Slot Volume
      </SectionTitle>
      <div style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
            <XAxis dataKey="slot" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} interval={0} angle={-35} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 10, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
            <Tooltip content={<MTooltip />} />
            <Bar dataKey="orders" name="Orders" radius={[4, 4, 0, 0]} maxBarSize={24}>
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.orders === peak ? T.accent : "rgba(210,58,59,0.4)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ─── Section: Booking Curve ──────────────────────────────────────────────────

function BookingCurve() {
  return (
    <Card style={{ flex: 2 }}>
      <SectionTitle subtitle="Cumulative orders from pre-order open to event day">
        Pre-Order Booking Curve
      </SectionTitle>
      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={MOCK.bookingCurve} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="curveGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={T.accent} stopOpacity={0.15} />
                <stop offset="95%" stopColor={T.accent} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
            <Tooltip content={<MTooltip />} />
            <Area type="monotone" dataKey="prior" name="2024" stroke={T.t3} strokeWidth={1.5} strokeDasharray="5 3" fill="none" dot={false} />
            <Area type="monotone" dataKey="current" name="2025" stroke={T.accent} strokeWidth={2.5} fill="url(#curveGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 8, justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 2, background: T.accent, borderRadius: 1 }} />
          <span style={{ fontSize: 10, color: T.t2 }}>2025 (612 orders)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 2, background: T.t3, borderRadius: 1, opacity: 0.5 }} />
          <span style={{ fontSize: 10, color: T.t3 }}>2024 (502 orders)</span>
        </div>
      </div>
    </Card>
  );
}

// ─── Section: Channel Mix Donut ──────────────────────────────────────────────

function ChannelMixDonut() {
  const data = MOCK.channelMix;
  return (
    <Card style={{ flex: 1 }}>
      <SectionTitle subtitle="Order source distribution">Channel Mix</SectionTitle>
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <div style={{ width: 140, height: 140, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={42} outerRadius={62} dataKey="value" strokeWidth={0}>
                {data.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
          {data.map((e, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: e.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: T.t2, flex: 1 }}>{e.name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{e.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ─── Section: Fulfillment Status ─────────────────────────────────────────────

function FulfillmentStatusCard() {
  return (
    <Card style={{ flex: 1 }}>
      <SectionTitle subtitle="Order lifecycle tracking">Fulfillment Status</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {MOCK.fulfillmentStatus.map((f, i) => (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <StatusPill status={f.status} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{f.count} ({f.pct}%)</span>
            </div>
            <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${f.pct}%`, background: f.color, borderRadius: 3, opacity: 0.7 }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Section: Customer Run Sheet ─────────────────────────────────────────────

function CustomerRunSheet() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const statuses = ["All", "Picked Up", "Ready", "Preparing", "Ordered", "No-Show", "Cancelled"];
  const filtered = MOCK.customerRunSheet.filter(c => {
    const matchSearch = !searchTerm || c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.id.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm);
    const matchStatus = statusFilter === "All" || c.fulfillment === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <Card>
      <SectionTitle subtitle="Printable customer order manifest for event-day execution">
        Customer Run Sheet
      </SectionTitle>

      {/* Search + Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.hover, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "6px 12px", flex: 1, maxWidth: 300 }}>
          <MIcon name="search" size={16} color={T.t3} />
          <input
            type="text"
            placeholder="Search name, phone, order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: "none", outline: "none", background: "transparent", fontSize: 12, color: T.t1, fontFamily: T.font, width: "100%" }}
          />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {statuses.map(s => (
            <FilterPill key={s} label={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", marginLeft: "auto" }}>
          <MIcon name="print" size={14} color={T.t3} />
          <span style={{ fontSize: 11, color: T.t2 }}>Print</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer" }}>
          <MIcon name="download" size={14} color={T.t3} />
          <span style={{ fontSize: 11, color: T.t2 }}>CSV</span>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font }}>
          <thead>
            <tr>
              {["Order", "Customer", "Phone", "Pickup Slot", "Items", "Total", "Payment", "Status"].map((h, i) => (
                <th key={i} style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr key={i} style={{ cursor: "pointer" }}>
                <td style={{ padding: "10px", fontSize: 11, fontWeight: 600, color: T.accent, borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>{row.id}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${T.divider}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{row.name}</div>
                  <div style={{ fontSize: 10, color: T.t3, marginTop: 1 }}>{row.email}</div>
                </td>
                <td style={{ padding: "10px", fontSize: 11, color: T.t2, borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>{row.phone}</td>
                <td style={{ padding: "10px", fontSize: 11, fontWeight: 600, color: T.t1, borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>{row.slot}</td>
                <td style={{ padding: "10px", fontSize: 11, color: T.t2, borderBottom: `1px solid ${T.divider}`, maxWidth: 240 }}>{row.items}</td>
                <td style={{ padding: "10px", fontSize: 12, fontWeight: 600, color: T.t1, borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>{row.total}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${T.divider}` }}><StatusPill status={row.payment} /></td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${T.divider}` }}><StatusPill status={row.fulfillment} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: T.t3, textAlign: "right" }}>
        Showing {filtered.length} of {MOCK.customerRunSheet.length} orders
      </div>
    </Card>
  );
}

// ─── Main Desktop Layout ─────────────────────────────────────────────────────

export default function EventsPreOrderDesktop() {
  return (
    <div style={{ display: "flex", fontFamily: T.font, background: T.bg, minHeight: "100vh", color: T.t1 }}>
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap" rel="stylesheet" />

      <Sidebar />

      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <TopBar />

        {/* Content Area */}
        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Row 1: KPI Cards */}
          <div style={{ display: "flex", gap: 16 }}>
            <KpiCard label="Event Revenue" value={MOCK.kpis.totalRevenue.value} delta={MOCK.kpis.totalRevenue.delta} up icon="payments" />
            <KpiCard label="Total Orders" value={MOCK.kpis.totalOrders.value} delta={MOCK.kpis.totalOrders.delta} up icon="receipt_long" />
            <KpiCard label="Avg Order Value" value={MOCK.kpis.avgOrderValue.value} delta={MOCK.kpis.avgOrderValue.delta} up icon="shopping_cart" />
            <KpiCard label="Fulfillment Rate" value={MOCK.kpis.fulfillmentRate.value} delta={MOCK.kpis.fulfillmentRate.delta} up icon="check_circle" />
            <KpiCard label="No-Shows" value={MOCK.kpis.noShows.value} delta={MOCK.kpis.noShows.delta} up icon="person_off" drillable />
            <KpiCard label="Cancellations" value={MOCK.kpis.cancellations.value} delta={MOCK.kpis.cancellations.delta} up icon="cancel" drillable />
          </div>

          {/* Row 2: Prep Quantity + Booking Curve */}
          <div style={{ display: "flex", gap: 16 }}>
            <PrepQuantityChart />
            <BookingCurve />
          </div>

          {/* Row 3: Pickup Slots */}
          <PickupSlotChart />

          {/* Row 4: Fulfillment Status + Channel Mix */}
          <div style={{ display: "flex", gap: 16 }}>
            <FulfillmentStatusCard />
            <ChannelMixDonut />
          </div>

          {/* Row 5: Customer Run Sheet */}
          <CustomerRunSheet />
        </div>
      </div>
    </div>
  );
}
